/**
 * Pure helpers for Statistics → Popular times and Job history (Backlog 062). No React, so they are unit-tested.
 */
import type { PopularityKnobDto, PopularityRecomputeRequest, SyntheticGenerateRequest } from '@api/types/popularity'
import type { StatisticsJobDto } from '@api/types/statistics'
import { JOB_STATUS } from '@api/types/adminJobs'

// ---------------------------------------------------------------- settings

export interface KnobGroup {
  key: string
  title: string
  help: string
  knobs: PopularityKnobDto[]
}

const GROUPS: { key: string; title: string; help: string; names: string[] }[] = [
  {
    key: 'privacy',
    title: 'Privacy',
    help: 'What the chart and the live label may reveal about recent presence.',
    names: ['MinLivePeople', 'PrivacyRecentHours'],
  },
  {
    key: 'activity',
    title: 'Activity counting',
    help: 'How runs become people present per hour, and how often the hourly table is rebuilt.',
    names: [
      'DefaultDwellMinutes', 'MinDwellSamples', 'DwellLookbackDays', 'OpenSessionGraceMinutes', 'MaxSessionHours',
      'RecomputeHours', 'LateArrivalLookbackDays', 'JobIntervalMinutes', 'NightlyUtcHour',
    ],
  },
  {
    key: 'typical',
    title: 'Typical week',
    help: 'How the chart averages past weeks, when it counts as sufficient, and the live busier / quieter label.',
    names: ['TypicalWeeks', 'TypicalDecay', 'MinDataWeeks', 'MinSlotSampleWeeks', 'DefaultTimeZone', 'BusierThreshold', 'QuieterThreshold'],
  },
  {
    key: 'scores',
    title: 'Scores',
    help: 'The 7- and 30-day popularity score behind the Popular sort. Real activity only, never synthetic.',
    names: ['ActivityWeight', 'InterestWeight'],
  },
  {
    key: 'trending',
    title: 'Trending',
    help: '"Trending now" on Base Camp and the Stories page.',
    names: [
      'TrendingWindowHours', 'TrendingActivityWeight', 'TrendingInterestWeight', 'TrendingCandidateCount', 'TrendingMaxTake',
      'TrendingCategoryBoost', 'TrendingTagBoost', 'TrendingMinHistory', 'TrendingHistoryDays', 'TrendingProfileCategories',
    ],
  },
  {
    key: 'retention',
    title: 'Retention',
    help: 'How long raw page-view rows are kept.',
    names: ['ViewRetentionDays'],
  },
]

/** The server's knobs in the page's groups, in the server's order inside each; unknown knobs land in "Other". */
export function groupKnobs(knobs: PopularityKnobDto[]): KnobGroup[] {
  const placed = new Set<string>()
  const groups: KnobGroup[] = GROUPS.map((g) => {
    const mine = knobs.filter((k) => g.names.includes(k.name))
    mine.forEach((k) => placed.add(k.name))
    return { key: g.key, title: g.title, help: g.help, knobs: mine }
  }).filter((g) => g.knobs.length > 0)
  const rest = knobs.filter((k) => !placed.has(k.name))
  if (rest.length > 0) groups.push({ key: 'other', title: 'Other', help: 'Settings this page does not group yet.', knobs: rest })
  return groups
}

/** "Max Session Hours" from "MaxSessionHours". */
export function knobLabel(name: string): string {
  return name.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/Utc/g, 'UTC')
}

/** Client-side check before the PUT (the server validates again, incl. cross-field rules). Null = fine. */
export function validateKnob(knob: PopularityKnobDto, raw: string): string | null {
  const value = raw.trim()
  if (value === '') return 'Required.'
  if (knob.dataType === 'string') return null
  const n = Number(value)
  if (!Number.isFinite(n)) return 'Must be a number.'
  if (knob.dataType === 'integer' && !Number.isInteger(n)) return 'Must be a whole number.'
  if (n < knob.min || n > knob.max) return `Must be between ${knob.min} and ${knob.max}.`
  return null
}

/** Only the knobs whose draft differs from the saved value (the PUT is partial). */
export function changedValues(knobs: PopularityKnobDto[], drafts: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {}
  for (const k of knobs) {
    const d = drafts[k.name]
    if (d === undefined) continue
    if (!sameValue(k, d, k.current)) out[k.name] = d.trim()
  }
  return out
}

export function sameValue(knob: PopularityKnobDto, a: string, b: string): boolean {
  if (knob.dataType === 'string') return a.trim() === b.trim()
  return Number(a) === Number(b)
}

// ---------------------------------------------------------------- recompute

export const MAX_BACKFILL_DAYS = 120

/**
 * The recompute body from the form's date inputs (yyyy-MM-dd, UTC days; `to` is inclusive so it becomes the next
 * midnight). Returns the problem instead when the range is not allowed.
 */
export function buildRecomputeRequest(
  from: string,
  to: string,
  typical: boolean,
  scores: boolean,
  now: Date = new Date(),
): { request?: PopularityRecomputeRequest; error?: string } {
  if (!from && to) return { error: 'Choose a start date with the end date, or leave both empty for the usual recent window.' }
  if (!from) return { request: { typical, scores } }
  const start = new Date(`${from}T00:00:00Z`)
  if (Number.isNaN(start.getTime())) return { error: 'The start date is not a date.' }
  let end = to ? new Date(`${to}T00:00:00Z`) : now
  if (Number.isNaN(end.getTime())) return { error: 'The end date is not a date.' }
  if (to) end = new Date(end.getTime() + 24 * 3600 * 1000)
  if (start.getTime() > now.getTime()) return { error: 'The range cannot start in the future.' }
  if (end.getTime() <= start.getTime()) return { error: 'The end date must be on or after the start date.' }
  const days = (end.getTime() - start.getTime()) / (24 * 3600 * 1000)
  if (days > MAX_BACKFILL_DAYS) return { error: `At most ${MAX_BACKFILL_DAYS} days can be recomputed at once (this is ${Math.ceil(days)}).` }
  return { request: { from: start.toISOString(), to: to ? end.toISOString() : undefined, typical, scores } }
}

// ---------------------------------------------------------------- synthetic data

export interface SyntheticForm {
  itemType: 'adventure' | 'story'
  itemId: string
  weeks: string
  peak: string
  weekdayCap: string
  weekendCap: string
  busiestStartHour: string
  busiestEndHour: string
  timeZone: string
}

export const DEFAULT_SYNTHETIC_FORM: SyntheticForm = {
  itemType: 'adventure',
  itemId: '',
  weeks: '6',
  peak: '10',
  weekdayCap: '',
  weekendCap: '',
  busiestStartHour: '',
  busiestEndHour: '',
  timeZone: '',
}

const optionalInt = (raw: string): number | null | 'bad' => {
  if (raw.trim() === '') return null
  const n = Number(raw)
  return Number.isInteger(n) ? n : 'bad'
}

/** The generate body from the form, or every problem (same rules as the server's `SyntheticSeedSpec.Validate`). */
export function buildSyntheticRequest(form: SyntheticForm): { request?: SyntheticGenerateRequest; errors: string[] } {
  const errors: string[] = []
  if (!form.itemId) errors.push('Pick an adventure or Story.')
  const weeks = Number(form.weeks)
  if (!Number.isInteger(weeks) || weeks < 1 || weeks > 26) errors.push('Weeks must be a whole number from 1 to 26.')
  const peak = Number(form.peak)
  if (!Number.isFinite(peak) || peak < 0.5 || peak > 500) errors.push('Peak must be between 0.5 and 500 people.')
  const weekdayCap = optionalInt(form.weekdayCap)
  const weekendCap = optionalInt(form.weekendCap)
  if (weekdayCap === 'bad' || (weekdayCap !== null && (weekdayCap < 0 || weekdayCap > 500))) errors.push('Weekday cap must be a whole number from 0 to 500.')
  if (weekendCap === 'bad' || (weekendCap !== null && (weekendCap < 0 || weekendCap > 500))) errors.push('Weekend cap must be a whole number from 0 to 500.')
  const start = optionalInt(form.busiestStartHour)
  const end = optionalInt(form.busiestEndHour)
  if (start === 'bad' || end === 'bad') errors.push('Busiest hours must be whole hours.')
  else if ((start === null) !== (end === null)) errors.push('Give both the start and the end of the busiest hours, or neither.')
  else if (start !== null && end !== null) {
    if (start < 0 || start > 23) errors.push('Busiest hours must start between 0 and 23.')
    if (end < 1 || end > 24) errors.push('Busiest hours must end between 1 and 24.')
    if (end <= start) errors.push('Busiest hours must end after they start.')
  }
  if (errors.length > 0) return { errors }
  return {
    errors,
    request: {
      itemType: form.itemType,
      itemId: form.itemId,
      weeks,
      peak,
      weekdayCap: weekdayCap as number | null,
      weekendCap: weekendCap as number | null,
      busiestStartHour: start as number | null,
      busiestEndHour: end as number | null,
      timeZone: form.timeZone.trim() || null,
    },
  }
}

/** "10am", "1pm", "midnight". */
export function hourLabel(hour: number): string {
  if (hour === 0 || hour === 24) return 'midnight'
  if (hour === 12) return 'noon'
  return hour < 12 ? `${hour}am` : `${hour - 12}pm`
}

// ---------------------------------------------------------------- job history

export function jobStatusLabel(status: number): { label: string; color: 'default' | 'info' | 'success' | 'error' | 'warning' } {
  switch (status) {
    case JOB_STATUS.Pending: return { label: 'Pending', color: 'default' }
    case JOB_STATUS.Running: return { label: 'Running', color: 'info' }
    case JOB_STATUS.Cancelled: return { label: 'Cancelled', color: 'warning' }
    case JOB_STATUS.Completed: return { label: 'Completed', color: 'success' }
    case JOB_STATUS.Failed: return { label: 'Failed', color: 'error' }
    default: return { label: 'Unknown', color: 'default' }
  }
}

export const isFinished = (status: number) =>
  status === JOB_STATUS.Completed || status === JOB_STATUS.Failed || status === JOB_STATUS.Cancelled

/** "00:01:02.5000000" (.NET TimeSpan) → "1m 2s"; "1.02:03:04" → "1d 2h 3m". */
export function formatDuration(raw?: string | null): string {
  if (!raw) return '-'
  const m = /^(?:(\d+)\.)?(\d{1,2}):(\d{2}):(\d{2})(?:\.(\d+))?$/.exec(raw)
  if (!m) return raw
  const [d, h, min, s] = [Number(m[1] ?? 0), Number(m[2]), Number(m[3]), Number(m[4])]
  if (d > 0) return `${d}d ${h}h ${min}m`
  if (h > 0) return `${h}h ${min}m`
  if (min > 0) return `${min}m ${s}s`
  const ms = m[5] ? Math.round(Number(`0.${m[5]}`) * 1000) : 0
  return s > 0 ? `${s}s` : `${ms}ms`
}

export interface JobMetadata {
  parameters?: Record<string, unknown> | null
  result?: Record<string, unknown> | null
  source?: string
}

export function parseMetadata(job: Pick<StatisticsJobDto, 'metadataJson'>): JobMetadata | null {
  if (!job.metadataJson) return null
  try {
    const parsed = JSON.parse(job.metadataJson)
    return parsed && typeof parsed === 'object' ? (parsed as JobMetadata) : null
  } catch {
    return null
  }
}

/** One line for a parameters / result object: "typical: yes · scores: no". */
export function summarize(obj?: Record<string, unknown> | null): string {
  if (!obj) return ''
  return Object.entries(obj)
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .map(([k, v]) => `${knobLabel(k.charAt(0).toUpperCase() + k.slice(1)).toLowerCase()}: ${formatValue(v)}`)
    .join(' · ')
}

function formatValue(v: unknown): string {
  if (typeof v === 'boolean') return v ? 'yes' : 'no'
  if (typeof v === 'number') return v.toLocaleString()
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) {
    const d = new Date(v)
    return Number.isNaN(d.getTime()) ? v : d.toLocaleString()
  }
  if (Array.isArray(v)) return `${v.length} item${v.length === 1 ? '' : 's'}`
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}

/** Human name of a history job type ("PopularityTypical" → "Popularity Typical"). */
export function jobTypeLabel(jobType: string, names: Record<string, string> = {}): string {
  return names[jobType] ?? knobLabel(jobType)
}
